'use client'
import React, { useState } from 'react'
import axios from 'axios'
import Heading from '@/app/components/atoms/Heading'
import Paragraph from '@/app/components/atoms/Paragraph'
import Section from '@/app/components/sections/Section'
import { AdvancedButton } from '../atoms/AdvancedButton'
import Circle from '../atoms/Circle'
import { clean } from '~/app/utils/sanitizeString'

const ContactFormSection = ({ data }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: '',
    honeyPot: '', // Honeypot field
  })

  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(false) // For handling loading state
  const [isSubmitted, setIsSubmitted] = useState(false) // For handling form visibility

  // Handle input changes
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()

    // Check honeypot field, if filled, consider it a bot submission
    if (formData.honeyPot) {
      console.log('Bot submission detected')
      return
    }

    setLoading(true) // Show loading state when submitting

    try {
      // Send the form data to the API
      const response = await axios.post('/api/send-email', formData)
      setStatus(
        'Tak, for at du har booket en demo af vores DKV-platform. <br/> <br/> Vi kontakter dig snarest for at aftale nærmere.',
      ) // Success message
      setIsSubmitted(true) // Hide form upon successful submission
      setFormData({
        firstName: '',
        lastName: '',
        company: '',
        email: '',
        phone: '',
        honeyPot: '',
      }) // Clear the form
    } catch (error) {
      console.error('Error sending email:', error)
      setStatus('Der skete dersværre en fejl.') // Error message
    } finally {
      setLoading(false) // Hide loading state after request completes
    }
  }

  // Conditional placeholder color logic
  const placeholderColorClass =
    clean(data.design?.color?.color) === 'lys'
      ? 'placeholder:text-rødtekst'
      : data.design?.color?.color === 'hvid'
        ? 'placeholder:text-rødtekst'
        : 'placeholder:text-lys/60'

  // Conditional placeholder color logic
  const borderColorClass =
  clean(data.design?.color?.color) === 'lys'
      ? 'border-b-rødtekst'
      : data.design?.color?.color === 'hvid'
        ? 'border-b-rødtekst'
        : 'border-b-lys/60'

  // Conditional placeholder color logic
  const highlightedWordsClass =
  clean(data.design?.color?.color) === 'lys'
      ? 'text-rød'
      : data.design?.color?.color === 'hvid'
        ? 'text-rød'
        : 'text-gul'

  // Conditional placeholder color logic
  const focusColorClass =
    data.design?.color?.color === 'lys'
      ? 'focus:placeholder:text-rødtekst'
      : data.design?.color?.color === 'hvid'
        ? 'focus:placeholder:text-rødtekst'
        : 'focus:placeholder:text-lys'

  return (
    <Section
      variant={clean(data.design?.color?.color)}
      paddingTop={clean(data.design?.padding?.spacingTop || 'default')}
      paddingBottom={clean(data.design?.padding?.spacingBottom || 'default')}
      className={`relative overflow-hidden `}
    >
      <Circle
        theme="light"
        className="absolute left-0 z-10 hidden rotate-90 -translate-y-full stroke-2 top-1/2 -translate-x-1/4 xs:block"
      />

      <div className="grid col-span-full md:grid-cols-2">
        <div className="col-span-1 pb-8 md:pr-20 prose-p:font-extralight">
          <Heading type="h3" className="" spacing="small">
            <span className="flex flex-wrap justify-center w-full md:justify-start gap-x-3 gap-y-3">
              {data.heading.split(' ').map((word, index) => (
                <span
                  key={index}
                  className={`inline-flex ${data?.hightlighted?.includes(index + 1) ? `${highlightedWordsClass}` : ''}`}
                >
                  {word}
                </span>
              ))}
            </span>
          </Heading>
          <Paragraph className="text-center font-extralight md:text-left">
            {data?.description}
          </Paragraph>
        </div>

        {/* Conditionally render the form or the success message */}
        {isSubmitted ? (
          <div className="mt-4">
            <Paragraph className="font-extralight">
              <span dangerouslySetInnerHTML={{ __html: status }} />
            </Paragraph>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5" id="ContactForm">
            <input
              type="text"
              name="firstName"
              placeholder="Fornavn*"
              value={formData.firstName}
              onChange={handleChange}
              className={`w-full text-center md:text-left pl-0 font-sans border-0 border-b font-extralight ${borderColorClass} bg-inherit ${placeholderColorClass} ${focusColorClass} `}
              required
            />
            <input
              type="text"
              name="lastName"
              placeholder="Efternavn"
              value={formData.lastName}
              onChange={handleChange}
              className={`w-full text-center md:text-left pl-0 font-sans border-0 border-b font-extralight ${borderColorClass} bg-inherit ${placeholderColorClass} ${focusColorClass}`}
            />
            <input
              type="text"
              name="company"
              placeholder="Virksomhed/organisation"
              value={formData.company}
              onChange={handleChange}
              className={`w-full text-center md:text-left pl-0 font-sans border-0 border-b font-extralight ${borderColorClass} bg-inherit ${placeholderColorClass} ${focusColorClass}`}
            />
            <input
              type="email"
              name="email"
              placeholder="Email*"
              value={formData.email}
              onChange={handleChange}
              className={`w-full text-center md:text-left pl-0 font-sans border-0 border-b font-extralight ${borderColorClass} bg-inherit ${placeholderColorClass} ${focusColorClass}`}
              required
            />
            <input
              type="tel"
              name="phone"
              placeholder="Telefon*"
              value={formData.phone}
              onChange={handleChange}
              className={`w-full text-center md:text-left pl-0 font-sans border-0 border-b font-extralight ${borderColorClass} bg-inherit ${placeholderColorClass} ${focusColorClass}`}
              required
            />

            {/* Honeypot input field */}
            <input
              type="text"
              name="honeyPot"
              value={formData.honeyPot}
              onChange={handleChange}
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
            />
            <div className="flex justify-center md:justify-start">
              <AdvancedButton
                type="submit"
                variant={
                  data.design?.color?.color === 'rød' ? 'secondary' : 'primary'
                }
                className="font-sans !mt-8"
                disabled={loading} // Disable the button while loading
              >
                {loading ? 'Sender...' : 'Bliv kontaktet'}{' '}
                {/* Show loading text */}
              </AdvancedButton>
            </div>
          </form>
        )}
      </div>
    </Section>
  )
}

export default ContactFormSection
